'use client'

import React, { useEffect, useState } from 'react'
import { useMakerFilter } from '@/hooks/use-maker-filter'
import Link from 'next/link'
import { searchMakers } from '@/apis/search-maker.service'
import { PostgrestError } from '@supabase/supabase-js'
import { SearchMakerCard } from '@/components/SearchMakerCard'
import { Search } from 'lucide-react'
import {
  FilterBarRow,
  FilterMainToggle,
  FilterChipRow,
  JobFilterModal,
  FilterMoreButton,
  FilterPanel,
  ExperienceFilterPanel,
} from '@/components/filters/FilterBar'

//TODO - 중북 추출 / ZOD 반영영
interface Maker {
  username: string
  main_job: string[] | null
  expertise: string[] | null
  bio: string
  user_id: string
  profile_id: string // 새로운 프로필 시스템
  is_active: boolean | null
  created_at: string
  updated_at: string
  deleted_at: string | null
  role: 'MAKER' | 'MANAGER' | 'NONE'
  account_work_experiences: any[]
}

const SearchMakersClient = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<PostgrestError | null>(null)
  const { filters, handleFilterChange } = useMakerFilter()
  const [makerList, setMakerList] = useState<Maker[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredMakerList, setFilteredMakerList] = useState<Maker[]>([])
  const [isJobFilterModalOpen, setIsJobFilterModalOpen] = useState(false)
  const [isFilterMoreExpanded, setIsFilterMoreExpanded] = useState(false)

  // 검색어로 필터링
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredMakerList(makerList)
    } else {
      const filtered = makerList.filter((maker) => {
        const searchLower = searchTerm.toLowerCase()
        return (
          maker.username?.toLowerCase().includes(searchLower) ||
          maker.bio?.toLowerCase().includes(searchLower) ||
          maker.main_job?.some((job) => job.toLowerCase().includes(searchLower)) ||
          maker.expertise?.some((exp) => exp.toLowerCase().includes(searchLower))
        )
      })
      setFilteredMakerList(filtered)
    }
  }, [searchTerm, makerList])

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await searchMakers({
          job: filters.job.length > 0 ? filters.job : undefined,
          specialization: filters.specialization.length > 0 ? filters.specialization : undefined,
          experience: filters.experience,
        })

        if (response.data && Array.isArray(response.data)) {
          setMakerList(response.data as unknown as Maker[])
        } else {
          setMakerList([])
        }
      } catch (err) {
        console.error('메이커 조회 예외:', err)
        setError(err as PostgrestError)
        setMakerList([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [filters])

  const hasActiveFilters = filters.job.length > 0 || filters.experience !== undefined || searchTerm.trim().length > 0

  const clearFilters = () => {
    setSearchTerm('')
    handleFilterChange('job', [])
    handleFilterChange('experience', undefined)
  }

  const filterChips = [
    ...filters.job.map((job) => ({
      key: `job-${job}`,
      label: job,
      tone: 'blue' as const,
      onClear: () => handleFilterChange('job', filters.job.filter((j) => j !== job)),
    })),
    ...(filters.experience
      ? [
          {
            key: 'experience',
            label: `경력: ${filters.experience[0]}~${filters.experience[1]}년`,
            tone: 'blue' as const,
            onClear: () => handleFilterChange('experience', undefined),
          },
        ]
      : []),
    ...(searchTerm.trim()
      ? [
          {
            key: 'search',
            label: `검색: ${searchTerm}`,
            tone: 'gray' as const,
            onClear: () => setSearchTerm(''),
          },
        ]
      : []),
  ]

  return (
    // ✅ 레이아웃이 컨테이너/패딩/상단 여백 담당 (여기서 mx/max-w/px/py 금지)
    <div className="w-full space-y-8">
      {/* 헤더 */}
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">메이커 찾기</h1>
        <p className="text-sm text-gray-600 leading-relaxed">프로젝트에 적합한 메이커를 찾아보세요</p>
      </header>

      {/* 검색 및 필터 섹션 */}
      <section className="space-y-3">
        {/* 검색창 */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="메이커 이름, 소개, 직무, 전문분야로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all bg-white text-sm"
          />
        </div>

        {/* 필터 바 */}
        <FilterBarRow
          leftContent={
            <JobFilterModal
              value={filters.job}
              onChange={(value) => handleFilterChange('job', value)}
              type="maker"
              open={isJobFilterModalOpen}
              onOpenChange={setIsJobFilterModalOpen}
              trigger={
                <FilterMainToggle
                  label="직무"
                  selectedLabel={
                    filters.job.length > 0
                      ? filters.job.length === 1
                        ? filters.job[0]
                        : `직무 ${filters.job.length}개`
                      : undefined
                  }
                  expanded={isJobFilterModalOpen}
                  onToggle={() => setIsJobFilterModalOpen((v) => !v)}
                  selected={filters.job.length > 0}
                />
              }
            />
          }
          rightContent={
            <FilterMoreButton
              expanded={isFilterMoreExpanded}
              onToggle={() => setIsFilterMoreExpanded((v) => !v)}
              label="필터 더보기"
              compact
            />
          }
        />

        {/* 선택된 필터 칩 */}
        <FilterChipRow chips={filterChips} />

        {/* 필터 더보기 패널 */}
        {isFilterMoreExpanded && (
          <FilterPanel>
            <ExperienceFilterPanel
              value={filters.experience}
              onChange={(value) => handleFilterChange('experience', value)}
            />
          </FilterPanel>
        )}
      </section>

      {/* 결과 통계 */}
      {!isLoading && !error && (
        <div className="text-sm text-gray-600">
          총 <strong className="text-gray-900 font-semibold">{filteredMakerList.length}</strong>명의 메이커를 찾았습니다
        </div>
      )}

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="flex items-center justify-center py-20 bg-white border border-gray-200 rounded-xl">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            <p className="text-sm text-gray-600">메이커를 불러오는 중...</p>
          </div>
        </div>
      )}

      {/* 에러 상태 */}
      {error && (
        <div className="text-center py-20 bg-white border border-gray-200 rounded-xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">오류가 발생했습니다</h3>
          <p className="text-sm text-gray-600">{error.message}</p>
        </div>
      )}

      {/* 메이커 목록 */}
      {!isLoading && !error && (
        <section className="space-y-4">
          {filteredMakerList.length === 0 ? (
            <div className="text-center py-20 bg-white border border-gray-200 rounded-xl">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">검색 결과가 없습니다</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {hasActiveFilters ? '다른 검색어나 필터로 시도해보세요' : '조건을 변경해보세요'}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm"
                  >
                    필터 초기화
                  </button>
                )}
              </div>
            </div>
          ) : (
            filteredMakerList.map((maker) => (
              <Link
                href={`/freelancer/profile/${maker.profile_id}`}
                key={maker.profile_id || maker.user_id}
                className="block"
              >
                <SearchMakerCard maker={maker} />
              </Link>
            ))
          )}
        </section>
      )}
    </div>
  )
}

export default SearchMakersClient
